import React from "react";
import styled from "@emotion/styled";

import {
  Box,
  Chip,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  IconButton as MuiIconButton,
} from "@mui/material";

import { useAuth } from "@auth/AuthContext";
import api from "@utils/axiosInstance";
import { radius, shadow } from "../../design-system/tokens";

// Fixed square so the hover/ripple halo stays a circle — IconButton's
// halo follows its box, and the rectangular flag would otherwise
// stretch it into an ellipse.
const IconButton = styled(MuiIconButton)`
  width: 44px;
  height: 44px;
`;

// The flag PNGs are 2:1-ish rectangles (e.g. ca.png is 100x50) — render at
// natural aspect ratio instead of the language dropdown's 22x22 circle,
// which squashes them.
const Flag = styled.img`
  border-radius: ${radius.sm};
  width: 28px;
  height: auto;
  display: block;
  box-shadow: ${shadow.ring};
`;

// Non-country tenants get a purpose-made flag instead of the ISO set.
// The DEMO tenant's identifier is "DE", which would otherwise resolve
// to the German flag.
const FLAG_OVERRIDES = {
  de: "checkered",
};

// LWI-1171 — tenant identifiers (CA, US, DE) double as ISO country codes,
// so the flag ships from the existing public flag set. Unknown identifiers
// fall back to a text chip so the indicator never silently disappears.
function TenantFlagIcon({ identifier, name }) {
  const [failed, setFailed] = React.useState(false);

  if (!identifier || failed) {
    return (
      <Chip label={identifier || "?"} size="small" sx={{ fontWeight: 600 }} />
    );
  }
  const code = identifier.toLowerCase();
  return (
    <Flag
      src={`/static/img/flags/${FLAG_OVERRIDES[code] || code}.png`}
      alt={name || identifier}
      onError={() => setFailed(true)}
    />
  );
}

// LWI-1171 — header flag showing the active tenant. Always visible; it only
// becomes a switcher menu when the user belongs to more than one tenant.
function NavbarTenantFlag() {
  const { isAuthenticated, tenantInfo, switchTenant } = useAuth();

  const [tenants, setTenants] = React.useState([]);
  const [anchorMenu, setAnchorMenu] = React.useState(null);
  const [switching, setSwitching] = React.useState(null);

  React.useEffect(() => {
    if (!isAuthenticated) return;
    api
      .get("/auth/tenants")
      .then(({ data }) => setTenants(data.tenants || []))
      // On failure the flag simply stays a static indicator.
      .catch(() => setTenants([]));
  }, [isAuthenticated]);

  if (!isAuthenticated || !tenantInfo?.identifier) {
    return null;
  }

  const canSwitch = tenants.length > 1;

  const closeMenu = () => {
    if (!switching) setAnchorMenu(null);
  };

  const handleSelect = async (identifier) => {
    if (switching || identifier === tenantInfo.identifier) return;
    setSwitching(identifier);
    try {
      await switchTenant(identifier);
      // Hard reload so every tenant-scoped fetch on the current page
      // re-runs under the new tenant token instead of showing stale data.
      window.location.reload();
    } catch {
      setSwitching(null);
    }
  };

  if (!canSwitch) {
    return (
      <Tooltip title={`Tenant: ${tenantInfo.name || tenantInfo.identifier}`}>
        <Box sx={{ display: "flex", alignItems: "center", px: 2 }}>
          <TenantFlagIcon
            identifier={tenantInfo.identifier}
            name={tenantInfo.name}
          />
        </Box>
      </Tooltip>
    );
  }

  return (
    <React.Fragment>
      <Tooltip
        title={`Tenant: ${
          tenantInfo.name || tenantInfo.identifier
        } — click to switch`}
      >
        <IconButton
          aria-owns={anchorMenu ? "tenant-menu-appbar" : undefined}
          aria-haspopup="true"
          onClick={(event) => setAnchorMenu(event.currentTarget)}
          color="inherit"
          size="large"
        >
          <TenantFlagIcon
            identifier={tenantInfo.identifier}
            name={tenantInfo.name}
          />
        </IconButton>
      </Tooltip>
      <Menu
        id="tenant-menu-appbar"
        anchorEl={anchorMenu}
        open={Boolean(anchorMenu)}
        onClose={closeMenu}
      >
        {tenants.map((t) => {
          const isActive = t.identifier === tenantInfo.identifier;
          return (
            <MenuItem
              key={t.identifier}
              selected={isActive}
              disabled={isActive || Boolean(switching)}
              onClick={() => handleSelect(t.identifier)}
            >
              <ListItemIcon>
                {switching === t.identifier ? (
                  <CircularProgress size={22} />
                ) : (
                  <TenantFlagIcon identifier={t.identifier} name={t.name} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={t.name || t.identifier}
                secondary={isActive ? "Currently active" : null}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </React.Fragment>
  );
}

export default NavbarTenantFlag;
